import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ComparisonPanel = ({ comparisonItems, onRemoveFromComparison, onClearComparison, isOpen, onToggle }) => {
  if (comparisonItems.length === 0) return null;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Comparison Panel */}
      <div className={`fixed bottom-0 left-0 right-0 lg:relative bg-white border-t lg:border border-accent-200 lg:rounded-2xl shadow-xl lg:shadow-soft z-50 lg:z-auto transform transition-transform duration-300 ${
        isOpen ? 'translate-y-0' : 'translate-y-full lg:translate-y-0'
      }`}>
        <div className="p-4 lg:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Icon name="GitCompare" size={20} className="text-secondary mr-2" />
              <h3 className="font-heading font-semibold text-lg text-accent-800">
                Compare Gifts ({comparisonItems.length})
              </h3>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearComparison}
                className="text-accent-600 hover:text-accent-800"
              >
                Clear All
              </Button>
              <button
                onClick={onToggle}
                className="lg:hidden p-2 rounded-lg text-accent-600 hover:text-accent-800 hover:bg-accent-100 transition-colors duration-200"
              >
                <Icon name="ChevronDown" size={20} />
              </button>
            </div>
          </div>

          {/* Comparison Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {comparisonItems.map((item) => (
              <div key={item.id} className="bg-surface rounded-xl p-4 relative">
                {/* Remove Button */}
                <button
                  onClick={() => onRemoveFromComparison(item.id)}
                  className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full shadow-soft flex items-center justify-center text-accent-600 hover:text-error hover:bg-error-50 transition-colors duration-200"
                >
                  <Icon name="X" size={12} />
                </button>

                {/* Item Image */}
                <div className="w-full h-24 bg-white rounded-lg overflow-hidden mb-3">
                  <Image
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Item Details */}
                <div>
                  <h4 className="font-medium text-accent-800 text-sm mb-1 line-clamp-2">
                    {item.name}
                  </h4>
                  <p className="text-xs text-accent-600 mb-2">{item.brand}</p>
                  
                  {/* Price */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-heading font-bold text-accent-800">${item.price}</span>
                    <div className="flex items-center text-xs text-success">
                      <Icon name="TrendingUp" size={12} className="mr-1" />
                      {item.successRate}%
                    </div>
                  </div>

                  {/* Key Features */}
                  <div className="space-y-1">
                    <div className="flex items-center text-xs text-accent-600">
                      <Icon name="Star" size={10} className="mr-1" />
                      <span>{item.rating}/5 rating</span>
                    </div>
                    <div className="flex items-center text-xs text-accent-600">
                      <Icon name="Truck" size={10} className="mr-1" />
                      <span>{item.shipping}</span>
                    </div>
                    <div className="flex items-center text-xs text-accent-600">
                      <Icon name="Users" size={10} className="mr-1" />
                      <span>{item.purchasedBy} bought</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-4 pt-4 border-t border-accent-200">
            <Button
              variant="primary"
              className="flex-1"
              iconName="BarChart3"
              iconPosition="left"
              onClick={() => console.log('View detailed comparison')}
            >
              Detailed Comparison
            </Button>
            <Button
              variant="outline"
              iconName="Share"
              onClick={() => console.log('Share comparison')}
              className="px-4"
            />
          </div>
        </div>
      </div>

      {/* Floating Toggle Button (Mobile) */}
      {!isOpen && comparisonItems.length > 0 && (
        <button
          onClick={onToggle}
          className="lg:hidden fixed bottom-4 right-4 bg-secondary text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 z-40"
        >
          <div className="flex items-center space-x-2">
            <Icon name="GitCompare" size={20} />
            <span className="font-medium">{comparisonItems.length}</span>
          </div>
        </button>
      )}
    </>
  );
};

export default ComparisonPanel;